import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';
import ApiService from '../services/ApiService';
import { DeleteAccountSchema } from '../Schemas';

function DeleteAccount() {
    const [loading, setLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(DeleteAccountSchema),
        mode: 'all'
    });

    const onSubmit = async (data) => {
        // Show a confirmation modal using SweetAlert2
        Swal.fire({
            title: 'Are you absolutely sure?',
            text: 'This action is permanent and cannot be undone. All your profile data, history, and messages will be permanently deleted.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ea580c',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            background: '#1f2937',
            color: '#f9fafb',
            customClass: {
                popup: 'swal2-dark-custom'
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    // Trigger the API endpoint to delete account
                    const api = new ApiService();
                    const response = await api.POST('/delete-account', {
                        email: data.email,
                        reason_text: data.reason_text
                    });

                    if (response.data && (response.data.status === '1')) {
                        Swal.fire({
                            title: 'Account Deleted',
                            text: 'Your account deletion request has been submitted.',
                            icon: 'success',
                            confirmButtonColor: '#3b82f6',
                            background: '#1f2937',
                            color: '#f9fafb'
                        }).then(() => {
                            // Redirect to landing or logout page
                            window.location.href = '/';
                        });
                    } else {
                        Swal.fire({
                            title: 'Error',
                            text: response.data?.message || 'Failed to delete your account. Please try again.',
                            icon: 'error',
                            confirmButtonColor: '#ef4444',
                            background: '#1f2937',
                            color: '#f9fafb'
                        });
                    }
                } catch (error) {
                    console.error('Delete account error:', error);
                    Swal.fire({
                        title: 'Error',
                        text: 'An unexpected error occurred. Please try again later.',
                        icon: 'error',
                        confirmButtonColor: '#ef4444',
                        background: '#1f2937',
                        color: '#f9fafb'
                    });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.header}>
                    <div style={styles.warningIconContainer}>
                        <svg
                            style={styles.warningIcon}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                    <h1 style={styles.title}>Delete Account</h1>
                    <p style={styles.subtitle}>
                        We are sorry to see you go. Please fill out the form below to request permanent deletion of your account.
                    </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
                    {/* Email Field */}
                    <div style={styles.formGroup}>
                        <label htmlFor="email" style={styles.label}>
                            Email Address
                        </label>
                        <input
                            type="email"
                            id="email"
                            placeholder="Enter your account email"
                            {...register('email')}
                            style={{
                                ...styles.input,
                                borderColor: errors.email ? '#ef4444' : '#e5e7eb'
                            }}
                        />
                        {errors.email && <span style={styles.errorText}>{errors.email.message}</span>}
                    </div>

                    {/* Reason Field */}
                    <div style={styles.formGroup}>
                        <label htmlFor="reason_text" style={styles.label}>
                            Reason for Leaving
                        </label>
                        <textarea
                            id="reason_text"
                            rows={4}
                            placeholder="Please let us know why you want to delete your account (feedback helps us improve)..."
                            {...register('reason_text')}
                            style={{
                                ...styles.textarea,
                                borderColor: errors.reason_text ? '#ef4444' : '#e5e7eb'
                            }}
                        />
                        {errors.reason_text && <span style={styles.errorText}>{errors.reason_text.message}</span>}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            ...styles.submitButton,
                            opacity: loading ? 0.7 : 1,
                            cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {loading ? 'Processing...' : 'Permanently Delete My Account'}
                    </button>
                </form>
            </div>
        </div>
    );
}


const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        padding: '24px',
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        background: '#f9fafb'
    },
    card: {
        background: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '480px',
        padding: '32px',
        border: '1px solid #f3f4f6',
        boxSizing: 'border-box'
    },
    header: {
        textAlign: 'center',
        marginBottom: '28px'
    },
    warningIconContainer: {
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        backgroundColor: '#fee2e2',
        color: '#ef4444',
        marginBottom: '16px'
    },
    warningIcon: {
        width: '28px',
        height: '28px'
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#111827',
        margin: '0 0 8px 0'
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        lineHeight: '1.5',
        margin: 0
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
    },
    label: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151'
    },
    input: {
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        color: '#111827',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        width: '100%',
        boxSizing: 'border-box'
    },
    textarea: {
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        color: '#111827',
        outline: 'none',
        resize: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        width: '100%',
        boxSizing: 'border-box'
    },
    errorText: {
        fontSize: '12px',
        color: '#ef4444',
        marginTop: '2px'
    },
    submitButton: {
        marginTop: '8px',
        padding: '14px',
        borderRadius: '8px',
        backgroundColor: '#dc2626',
        color: '#ffffff',
        fontSize: '15px',
        fontWeight: '600',
        border: 'none',
        transition: 'background-color 0.2s, transform 0.1s',
        boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.2)'
    }
};

export default DeleteAccount;